<template>
    <div v-if="animationsEnabled" class="vrm-theme-background__animation">
        <rive-animation
            ref="animation"
            class="desktop-only"
            src="/anim/christmas.riv"
            alignment="bottomLeft"
            fit="fitWidth"
            :state-machines="['Main']"
            :autoplay="false"
            @load="onLoad"
            @state-change="onStateChange"
        ></rive-animation>

        <img class="mobile-only" :src="christmasThemeImageSource" alt="Christmas illustration" />
    </div>

    <div v-else class="vrm-theme-background__image">
        <img :src="christmasThemeImageSource" alt="Christmas illustration" />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { ngInject } from '@/vue/decorators/angular-interop';
import RiveAnimation from '@/vue/components/shared/anim/RiveAnimation';

@Component({
    components: {
        RiveAnimation,
    },
    data: function() {
        return {
            rive: undefined,
            lastMouseMove: undefined,
            position: undefined,
            windowWidth: window.innerWidth,
        };
    },
})
export default class VrmChristmasAnimation extends Vue {
    @ngInject()
    AppearanceService;

    @ngInject()
    vrmThemes;

    /**
     * Called after the component and DOM have been initialized.
     *
     * Set up some listeners to handle resizing and mobile interactions properly.
     */
    mounted() {
        window.addEventListener('resize', this.onResize);
        document.addEventListener('scroll', this.onScroll);
        document.addEventListener('mousemove', this.onMouseMove);
    }

    /**
     * Called before the component has been destroyed.
     *
     * Removes the event listeners.
     */
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('mousemove', this.onMouseMove);
        if (this.rive) {
            this.rive.cleanup();
        }
    }

    /**
     * Called when the Rive animation is loaded.
     *
     * Initialize any listeners needed for the animation.
     *
     * @param e
     */
    onLoad(e) {
        if (e.rive) {
            this.rive = e.rive;
        }

        this.isPlaying = false;
    }

    /**
     * Resize the Rive drawing surface if the window size changes.
     *
     */
    onResize() {
        if (this.rive) {
            // Resize canvas to improve resolution.
            this.rive.resizeDrawingSurfaceToCanvas();
        }

        this.windowWidth = window.innerWidth;
        this.position = this.$refs.animation.$el.getBoundingClientRect();
        this.checkHover();
    }

    onScroll() {
        this.checkHover();
    }

    onMouseMove(e) {
        this.lastMouseMove = e;
        this.checkHover();
    }

    /**
     * Check if the mouse is over the animation canvas based on the element position and mouse move position.
     *
     * Needed because space for backgrounds is created using a padding on the content which will always overlap the
     * animation canvas.
     */
    checkHover() {
        if (this.$refs.animation) {
            this.position = this.$refs.animation.$el.getBoundingClientRect();
        }

        if (this.lastMouseMove !== undefined && this.position !== undefined) {
            const minX = this.position.left;
            const minY = this.position.top;
            const maxX = this.position.right;
            const maxY = this.position.bottom;

            const mouseX = this.lastMouseMove.clientX;
            const mouseY = this.lastMouseMove.clientY;

            const matchX = mouseX > minX && mouseX < maxX;
            const matchY = mouseY > minY && mouseY < maxY;

            if (matchX && matchY) {
                this.playAnimation();
            }
        }
    }

    playAnimation() {
        if (this.rive && !this.isPlaying) {
            this.rive.play('Main');
            this.isPlaying = true;
        }
    }

    onStateChange(e) {
        if (e.data && e.data.includes('exit')) {
            this.rive.stop('Main');
            this.isPlaying = false;
        }
    }

    /**
     * Check if animations are enabled in the config.
     *
     * @returns {*|boolean}
     */
    get animationsEnabled() {
        return this.AppearanceService.isChristmasThemeAnimationsActive();
    }

    get christmasThemeImageSource() {
        //Smaller image for desktop
        return this.windowWidth > 1280
            ? '/img/themes/christmas/bg-christmas-small.svg'
            : '/img/themes/christmas/bg-christmas.svg';
    }
}
</script>
